<template>
  <asc-page-loading-indicator
    :loading="isAdminLoading && apiPageFailure.length === 0"
  >
    <v-container fluid class="asc-admin-container--container">
      <asc-page-header>
        <template #pageTitle><asc-page-title :title="getTitle" /></template>
      </asc-page-header>
      <asc-tab v-if="$route.name !== 'Feature Flags'" v-model="activeTab">
        <asc-tab-title
          v-for="tab in tabs"
          :key="tab.id"
          :to="tab.route"
          :title="tab.displayName"
        ></asc-tab-title>
      </asc-tab>
      <router-view
        :user="profile"
        :on-footer-action="helpAboutFooterAction"
        @onHelpAboutGridSelectionChange="handleHelpAboutSelectionChange"
        @onFooterActionDisabled="handleFooterActionDisabled"
        @OnFooterActionComplete="handleFooterActionComplete"
      ></router-view>

      <asc-page-fixed-footer
        v-if="
          helpAboutGridDataLength &&
          helpAboutSelectedRows &&
          helpAboutSelectedRows.length > 0
        "
        class="helpAboutSelectionFooter"
      >
        <v-row no-gutters cols="12">
          <v-col class="action d-flex justify-end">
            <div class="d-flex justify-end">
              <asc-accent-button
                button-text="Cancel"
                value="cancel"
                :disabled="footerActionDisabled"
                outline
                secondary
                :on-click="handleFooterAction"
                class="mr-3"
              ></asc-accent-button>
              <asc-accent-button
                button-text="Delete Selected"
                value="delete"
                :disabled="footerActionDisabled"
                :on-click="handleFooterAction"
                class="mr-5"
              ></asc-accent-button>
              <span class="mt-1">{{ getHelpAboutSelectionDetails }}</span>
            </div>
          </v-col>
        </v-row>
      </asc-page-fixed-footer>
    </v-container>
  </asc-page-loading-indicator>
</template>

<script>
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import AscPageFixedFooter from '@/components/molecules/AscPageFixedFooter'
import AscTab from '@/components/atoms/AscTab'
import AscTabTitle from '@/components/atoms/AscTabTitle'
import AscPageHeader from '@/components/atoms/AscPageHeader'
import AscPageTitle from '@/components/atoms/AscPageTitle'
import AscAccentButton from '@/components/atoms/AscAccentButton'

import { mapState, mapActions, mapGetters } from 'vuex'

import { USER_CONSTS as UC } from '@/stores/consts'

import AppFeatureFlags from '@/utilities/appFeatureFlags'

export default {
  name: 'AscAdminContainer',

  components: {
    AscPageLoadingIndicator,
    AscPageFixedFooter,
    AscTab,
    AscTabTitle,
    AscPageHeader,
    AscPageTitle,
    AscAccentButton,
  },

  data() {
    return {
      isAdminLoading: false,
      activeTab: '',
      tabs: [],
      helpAboutSelectedRows: [],
      helpAboutGridDataLength: 0,
      helpAboutFooterAction: null,
      footerActionDisabled: false,
    }
  },

  computed: {
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(UC.NAMESPACE, ['profile', 'roles']),
    ...mapGetters(UC.NAMESPACE, [
      'isAdmin',
      'isUserManager',
      'isAnalyticAdmin',
      'isDocumentManager',
      'isAirlineManager',
      'isUserReader',
    ]),
    getTitle() {
      return this.$route.name === 'Feature Flags' ? 'Feature Flags' : 'Admin'
    },
    getHelpAboutSelectionDetails() {
      return `(${this.helpAboutSelectedRows.length} of ${this.helpAboutGridDataLength})`
    },
    isDocManagementActive() {
      return AppFeatureFlags.getDocumentManagementPage()
    },
  },

  watch: {
    '$route.name': {
      handler(newValue) {
        if (newValue === 'Admin' && this.tabs[0]) {
          this.$router.push({ name: `${this.tabs[0].routeName}` })
        }
      },
    },
    helpAboutSelectedRows(newValue) {
      const QSISliderElement = document.querySelector('.QSISlider')
      if (QSISliderElement) {
        QSISliderElement.style.display =
          newValue && newValue.length ? 'none' : ''
      }
    },
  },

  mounted() {
    this.isAdminLoading = true
    if (!this.profile) this.getUserProfile()
    this.getTabs()
    this.isAdminLoading = false
  },

  methods: {
    ...mapActions(UC.NAMESPACE, [UC.GET_USER_PROFILE]),
    async getUserProfile() {
      await this[UC.GET_USER_PROFILE]()
    },
    getTabs() {
      if (this.isAnalyticAdmin) {
        this.tabs.push({
          id: 1,
          displayName: 'Analytic Mgmt',
          route: `/admin/analytic`,
          routeName: 'Analytic Management',
        })
      }
      if (this.isAirlineManager) {
        this.tabs.push({
          id: 2,
          displayName: 'Airline Mgmt',
          route: `/admin/airline`,
          routeName: 'Airline Management',
        })
      }
      if (this.isDocumentManager && this.isDocManagementActive) {
        this.tabs.push({
          id: 3,
          displayName: 'Document Mgmt',
          route: `/admin/document`,
          routeName: 'Document Management',
        })
      }
      if (this.isUserManager || this.isUserReader) {
        this.tabs.push({
          id: 4,
          displayName: 'User Mgmt',
          route: `/admin/user`,
          routeName: 'User Management',
        })
      }

      if (this.tabs[0] && this.$route.name === 'Admin') {
        this.$router.push({ name: `${this.tabs[0].routeName}` })
      }
    },

    handleHelpAboutSelectionChange(selectedRows, dataLength) {
      this.helpAboutFooterAction = null
      this.helpAboutSelectedRows = selectedRows
      this.helpAboutGridDataLength = dataLength
    },

    handleFooterActionDisabled(value) {
      this.footerActionDisabled = value
    },

    handleFooterActionComplete(action) {
      // Clear selections: grid.deselect is not working as the grid is updated on delete
      if (action === 'delete') this.helpAboutSelectedRows = []
    },

    handleFooterAction(e, value) {
      if (value) {
        this.helpAboutFooterAction = {
          action: value,
          selectedRows: this.helpAboutSelectedRows,
        }
      }
    },
  },
}
</script>

<style lang="scss">
.asc-admin-container--container {
  background-color: $gray-100;

  &.container {
    padding: 16px;
  }
}
</style>
